/**
 * Organization types, roles and permissions (master-spec §E, §P; Master Prompt §15).
 * Roles are fixed in code for the MVP and combinable on a membership.
 * Permissions are what endpoints check; roles are never checked directly.
 * Each later phase adds its own permissions from the Permission Matrix (master-spec §P).
 */

export const ORG_TYPES = ['platform', 'clinic', 'supplier'] as const;
export type OrgType = (typeof ORG_TYPES)[number];

export const ROLES = [
  'platform_admin',
  'clinic_admin',
  'doctor',
  'receptionist',
  // The laser section (ADR-036): its own desk, and a non-doctor practitioner of laser visits.
  'laser_receptionist',
  'laser_operator',
  'supplier_admin',
  'supplier_staff',
] as const;
export type Role = (typeof ROLES)[number];

export const ROLES_BY_ORG_TYPE: Readonly<Record<OrgType, readonly Role[]>> = {
  platform: ['platform_admin'],
  clinic: ['clinic_admin', 'doctor', 'receptionist', 'laser_receptionist', 'laser_operator'],
  supplier: ['supplier_admin', 'supplier_staff'],
};

/** The role that administers members of an organization of each type. */
export const ADMIN_ROLE_BY_ORG_TYPE: Readonly<Record<OrgType, Role>> = {
  platform: 'platform_admin',
  clinic: 'clinic_admin',
  supplier: 'supplier_admin',
};

export function isRoleAllowedForOrgType(role: Role, orgType: OrgType): boolean {
  return ROLES_BY_ORG_TYPE[orgType].includes(role);
}

export const PERMISSIONS = [
  'org.read',
  'members.read',
  'members.manage',
  'audit.read',
  // Clinic Core (docs/clinic-core/design.md §4)
  'patients.read',
  'patients.write',
  'medical.read',
  'medical.write',
  'appointments.read',
  'appointments.manage',
  'visits.read',
  'visits.manage',
  'settings.manage',
  'sms.send',
  // A doctor's own treatment methods (ADR-028)
  'methods.manage',
  // A doctor's own booking preferences, e.g. allowing overlaps (ADR-034)
  'practitioner_settings.manage_own',
  // Platform: the methods suggested to every doctor (ADR-028)
  'system_methods.manage',
  // Clinic Inventory (docs/clinic-inventory/design.md §4)
  'inventory.read',
  'inventory.receive',
  'inventory.manage',
  'inventory.trace',
  // Seeing and entering purchase prices (ADR-053)
  'inventory.cost',
  // Clinic Finance (docs/clinic-finance/design.md §4)
  'balances.read',
  'payments.record',
  'finance.read',
  'finance.manage',
  'earnings.read_own',
  // Platform: the nightly reconciliation (§Y 3.4)
  'reconciliation.read',
  // Platform: the clinics panel (ADR-045)
  'clinics.read',
  'clinics.manage',
  // Platform: the central GTIN catalog (ADR-055); seeing which clinic proposed a GTIN is separate
  'catalog.review',
  'catalog.sources',
  // Platform: stock insights (ADR-056) and each clinic's sharing basis
  'insights.read',
  'insights.clinic_read',
  'data_sharing.manage',
  // Migration Tools (docs/migration-tools/design.md §4)
  'patients.import',
  'data.export',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

export const ROLE_PERMISSIONS: Readonly<Record<Role, readonly Permission[]>> = {
  platform_admin: [
    'org.read',
    'members.read',
    'members.manage',
    'audit.read',
    'system_methods.manage',
    'reconciliation.read',
    'clinics.read',
    'clinics.manage',
    'catalog.review',
    'catalog.sources',
    'insights.read',
    'insights.clinic_read',
    'data_sharing.manage',
  ],
  // Clinic admin runs the clinic but sees no clinical data unless the membership also has `doctor`.
  clinic_admin: [
    'org.read',
    'members.read',
    'members.manage',
    'audit.read',
    'patients.read',
    'patients.write',
    'appointments.read',
    'appointments.manage',
    'visits.read',
    'settings.manage',
    'sms.send',
    'inventory.read',
    'inventory.receive',
    'inventory.manage',
    'inventory.trace',
    'inventory.cost',
    'balances.read',
    'payments.record',
    'finance.read',
    'finance.manage',
    'patients.import',
    'data.export',
  ],
  doctor: [
    'org.read',
    'patients.read',
    'patients.write',
    'medical.read',
    'medical.write',
    'appointments.read',
    'appointments.manage',
    'visits.read',
    'visits.manage',
    'methods.manage',
    'practitioner_settings.manage_own',
    'inventory.read',
    'inventory.receive',
    'inventory.trace',
    'inventory.cost',
    'balances.read',
    'payments.record',
    'earnings.read_own',
  ],
  // Master Prompt §16: basic profile, contact, appointments and balance; never medical data.
  receptionist: [
    'org.read',
    'patients.read',
    'patients.write',
    'appointments.read',
    'appointments.manage',
    'visits.read',
    'visits.manage',
    'sms.send',
    'balances.read',
    'payments.record',
  ],
  // ADR-036: the receptionist's permissions; the sections they apply to come from app.member_sections.
  laser_receptionist: [
    'org.read',
    'patients.read',
    'patients.write',
    'appointments.read',
    'appointments.manage',
    'visits.read',
    'visits.manage',
    'sms.send',
    'balances.read',
    'payments.record',
  ],
  // ADR-036: a practitioner of laser visits only (RLS); no stock at all, no front-desk money.
  laser_operator: [
    'org.read',
    'patients.read',
    'patients.write',
    'medical.read',
    'medical.write',
    'appointments.read',
    'appointments.manage',
    'visits.read',
    'visits.manage',
    'methods.manage',
    'practitioner_settings.manage_own',
    'earnings.read_own',
  ],
  supplier_admin: ['org.read', 'members.read', 'members.manage', 'audit.read'],
  supplier_staff: ['org.read'],
};

/**
 * Areas of work the clinic admin ticks per member, on top of roles (ADR-042). Each permission is in
 * at most one area, and every clinic role holds whole areas, so a role's areas are its defaults.
 * Never in an area (always from the roles): managing members (else a member could grant themselves
 * anything), being a practitioner (doing visits, methods, own booking settings, own earnings) and
 * `org.read`.
 */
export const ACCESS_AREAS = [
  'patients',
  'medical',
  'appointments',
  'sms',
  'cash',
  'finance',
  'inventory',
  'inventory_receive',
  'inventory_cost',
  'inventory_trace',
  'inventory_manage',
  'settings',
  'data',
  'audit',
] as const;
export type AccessArea = (typeof ACCESS_AREAS)[number];

export const AREA_PERMISSIONS: Readonly<Record<AccessArea, readonly Permission[]>> = {
  patients: ['patients.read', 'patients.write'],
  medical: ['medical.read', 'medical.write'],
  appointments: ['appointments.read', 'appointments.manage', 'visits.read'],
  sms: ['sms.send'],
  cash: ['balances.read', 'payments.record'],
  finance: ['finance.read', 'finance.manage'],
  inventory: ['inventory.read'],
  inventory_receive: ['inventory.receive'],
  inventory_cost: ['inventory.cost'],
  inventory_trace: ['inventory.trace'],
  inventory_manage: ['inventory.manage'],
  settings: ['settings.manage'],
  data: ['patients.import', 'data.export'],
  audit: ['audit.read'],
};

const AREA_OF = new Map<Permission, AccessArea>(
  ACCESS_AREAS.flatMap((area) => AREA_PERMISSIONS[area].map((p) => [p, area] as const)),
);

/** The areas a set of roles gives when the admin has not ticked anything (ADR-042). */
export function defaultAreasOf(roles: readonly Role[]): AccessArea[] {
  const held = new Set(roles.flatMap((r) => ROLE_PERMISSIONS[r]));
  return ACCESS_AREAS.filter((area) => AREA_PERMISSIONS[area].every((p) => held.has(p)));
}

/**
 * A member's permissions: the roles' permissions, except that when the admin chose areas (not null)
 * every permission that belongs to an area comes from those areas instead. The database computes the
 * same in `app.current_member_can` (an integration test keeps the two equal).
 */
export function effectivePermissions(roles: readonly Role[], areas: readonly AccessArea[] | null): Set<Permission> {
  const out = new Set<Permission>();
  for (const r of roles) {
    for (const p of ROLE_PERMISSIONS[r]) if (areas === null || !AREA_OF.has(p)) out.add(p);
  }
  for (const a of areas ?? []) for (const p of AREA_PERMISSIONS[a]) out.add(p);
  return out;
}

/**
 * Roles that may read and write clinical data. Row-level security repeats this list in SQL
 * (ADR-026, `app.current_member_has_role`); an integration test keeps the two identical.
 */
export const CLINICAL_ROLES: readonly Role[] = ROLES.filter((r) => ROLE_PERMISSIONS[r].includes('medical.read'));

/** Roles that can be the practitioner of an appointment or a visit: doctors anywhere, operators in laser (ADR-036). */
export const PRACTITIONER_ROLES: readonly Role[] = ['doctor', 'laser_operator'];

/**
 * Sections of a clinic (ADR-036). A service belongs to one; its appointments, visits, packages and
 * charges copy it. Who reaches which section is computed by `app.member_sections` in SQL only.
 */
export const CLINIC_SECTIONS = ['general', 'laser'] as const;
export type ClinicSection = (typeof CLINIC_SECTIONS)[number];

/**
 * Where a member may be the practitioner: a doctor everywhere, a laser operator in laser. The
 * database repeats it in `app.practitioner_eligible` (appointment and visit triggers).
 */
export function practitionerSections(roles: readonly Role[]): ClinicSection[] {
  if (roles.includes('doctor')) return [...CLINIC_SECTIONS];
  return roles.includes('laser_operator') ? ['laser'] : [];
}

/** What a section grants: the desk (appointments, visits), the till (balances, payments), the clinical record. */
export const SECTION_KINDS = ['desk', 'cash', 'clinical'] as const;
export type SectionKind = (typeof SECTION_KINDS)[number];

/** The receptionist roles a clinic admin may give the laser desk or the laser till to. */
export const LASER_STAFF_ROLES = ['laser_receptionist', 'receptionist'] as const satisfies readonly Role[];
export type LaserStaffRole = (typeof LASER_STAFF_ROLES)[number];

/** Union of the permissions of all roles held on one membership. */
export function permissionsForRoles(roles: readonly Role[]): ReadonlySet<Permission> {
  const result = new Set<Permission>();
  for (const role of roles) {
    for (const permission of ROLE_PERMISSIONS[role]) result.add(permission);
  }
  return result;
}
